import type { PaymentPlatform } from '../types'
import type { DeployEnv } from './monthly-no-trial'
import type { Product, SubscriptionProduct } from './types'

export interface AnnualDoubleTrialConfig {
  product_id: number
  sku: string
}

// Arm A rescue product (annual + 14-day trial). Service-API product_id per env
// (production = 67; staging = 64; dev = 98). china_android is null — the rescue
// offer is iOS / Google Play only. Source: MONEY-928.
export const ANNUAL_DOUBLE_TRIAL: Record<
  DeployEnv,
  Record<PaymentPlatform, AnnualDoubleTrialConfig | null>
> = {
  production: {
    ios: { product_id: 67, sku: 'forest_annual_early_bird_2' },
    googleplay_android: {
      product_id: 67,
      sku: 'forest_plus-annual-early-bird-2',
    },
    china_android: null,
  },
  staging: {
    ios: { product_id: 64, sku: 'forest_annual_early_bird_2' },
    googleplay_android: {
      product_id: 64,
      sku: 'forest_plus-annual-early-bird-2',
    },
    china_android: null,
  },
  dev: {
    ios: { product_id: 98, sku: 'forest_annual_early_bird_2' },
    googleplay_android: {
      product_id: 98,
      sku: 'forest_plus-annual-early-bird-2',
    },
    china_android: null,
  },
}

/**
 * Builds the Arm A "rescue" product (annual plan with a doubled, 14-day trial)
 * from a hardcoded `{ product_id, sku }` config, merging price / offers /
 * trial-days from the native subscription product when available. Mirrors
 * `buildMonthlyNoTrial`; the trial discount is a native-applied introductory
 * offer, so no `offer_id` is selected here.
 */
export function buildAnnualDoubleTrial(
  config: AnnualDoubleTrialConfig,
  subscriptionProducts: SubscriptionProduct[],
): Product {
  const nativeProduct = subscriptionProducts.find((p) => p.sku === config.sku)
  return {
    product_id: config.product_id,
    receipt_system_product_id: 0,
    sku: config.sku,
    product_type: 'subscription',
    duration: 'yearly',
    eligible_trial_days: nativeProduct?.eligible_trial_days ?? 0,
    offers: nativeProduct?.offers,
    price_localized: nativeProduct?.price_localized ?? '',
    price_value: nativeProduct?.price_value ?? 0,
    display_name: nativeProduct?.display_name ?? '',
  }
}
